import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { getKeywordBasedSearch, getShortTermSearch } from '../../../../api/services/hostService';
import { IShortTermProperty } from '../../../model/guestModel';
import PropertyCard, { genderTypeConfig } from '../../../../components/PropertyCard/PropertyCard';
import {
  MapPin,
  Star,
  Wifi,
  UtensilsCrossed,
  Users,
  Heart,
  Filter,
  SlidersHorizontal,
  ArrowUpDown,
  Bed,
  Dumbbell,
  Car,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  Building2,
  Snowflake,
  Shirt,
  Shield,
  Train,
  ShoppingBag,
  Hospital,
  GraduationCap,
  Laptop,
  BadgeCheck,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../../../components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Badge } from "../../../../components/ui/badge";
import { Slider } from "../../../../components/ui/slider";
import { Checkbox } from "../../../../components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import { Label } from '../../../../components/ui/label';
import { motion } from "framer-motion";

import { useUserContext } from "../../../protectedRoute/UserContext";
import LandingHeader from '../../../../components/landing/Header';
import PreLoginHeader from '../../../../components/PreLoginHeader/PreLoginHeader';
import LandingPageHeader from '../../../../components/LandingPageHeader/LandingPageHeader';
import Footer from '../../../../components/landing/Footer';

const DEFAULT_PROPERTY_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&auto=format&fit=crop&q=60";

const amenityIcons: Record<string, React.ElementType> = {
  wifi:     Wifi,
  ac:       Snowflake,
  parking:  Car,
  laundry:  Shirt,
  gym:      Dumbbell,
  security: Shield,
};

const amenityLabels: Record<string, string> = {
  wifi:     "WiFi",
  ac:       "AC",
  parking:  "Parking",
  laundry:  "Laundry",
  gym:      "Gym",
  security: "Security",
};

const HIGHLIGHTS: { value: string; label: string; icon: React.ElementType }[] = [
  { value: "metro-nearby",    label: "Metro Nearby",    icon: Train },
  { value: "market-nearby",   label: "Market Nearby",   icon: ShoppingBag },
  { value: "hospital-nearby", label: "Hospital Nearby", icon: Hospital },
  { value: "college-nearby",  label: "College Nearby",  icon: GraduationCap },
  { value: "it-park",         label: "IT Park Nearby",  icon: Laptop },
];

// Form stores lowercase: "pg" | "hostel"
const PROPERTY_TYPES = [
  { value: "pg",     label: "PG" },
  { value: "hostel", label: "Hostel" },
];
const RATING_OPTIONS = [
  { value: 4.5, label: "4.5+ Excellent" },
  { value: 4.0, label: "4.0+ Very Good" },
  { value: 3.5, label: "3.5+ Good" },
  { value: 3.0, label: "3.0+ Fair" },
];

interface PropertyItem {
  id: string;
  name: string;
  location: { street_address?: string; locality?: string; city?: string; };
  beds: { price_per_bed?: string; }[];
  rooms: { price_per_room?: string; }[];
  images: { image?: string; }[];
  average_rating: number;
  price: string;
  is_verified: boolean;
  amenities: string[];
  bedType?: string;       // maps to property_type
  badge?: string;
  originalPrice?: string;
  rating: number;
  reviews: number;
  genderType?: string;    // property_gender_type: "male" | "female" | "unisex"
  foodAvailable?: boolean;
  highlights?: string[];
  city?: string;
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-8 pt-6 border-t border-border/50">
      <p className="text-sm text-muted-foreground order-2 sm:order-1">
        Showing <span className="font-medium text-foreground">{startItem}–{endItem}</span> of{' '}
        <span className="font-medium text-foreground">{totalItems}</span> properties
      </p>
      <div className="flex items-center gap-1 order-1 sm:order-2">
        <Button
          variant="outline"
          size="sm"
          className="h-9 w-9 p-0"
          disabled={currentPage === 1}
          onClick={() => { onPageChange(currentPage - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <ChevronLeft size={16} />
        </Button>
        {getPageNumbers().map((page, idx) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground text-sm">…</span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              className={`h-9 w-9 p-0 ${currentPage === page ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => { onPageChange(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            >
              {page}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-9 w-9 p-0"
          disabled={currentPage === totalPages}
          onClick={() => { onPageChange(currentPage + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
        >
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
};

const GuestSearchResults: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = useUserContext();

  const [favorites, setFavorites] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState([0, 1000]);
  const [sortBy, setSortBy] = useState("recommended");
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>([]);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [selectedGenderType, setSelectedGenderType] = useState<string>('');
  const [foodAvailable, setFoodAvailable] = useState<boolean | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedHighlights, setSelectedHighlights] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [shortTermPage, setShortTermPage] = useState(1);

  const ITEMS_PER_PAGE = 12;

  const [keyword, setKeyword] = useState<string>("");
  const [maleGuests, setMaleGuests] = useState<number>(0);
  const [femaleGuests, setFemaleGuests] = useState<number>(0);
  const [checkInDate, setCheckInDate] = useState<string>("");
  const [checkOutDate, setCheckOutDate] = useState<string>("");
  const [accommodationType, setAccommodationType] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const localDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const today = localDate(new Date());
  const tomorrow = localDate(new Date(Date.now() + 86400000));

  const [properties, setProperties] = useState<PropertyItem[]>([]);
  const [shortTermProperties, setShortTermProperties] = useState<IShortTermProperty[]>([]);
  const [shortTermLoading, setShortTermLoading] = useState(false);

  const toggleFavorite = (id: string) => {
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );
  };

  const toggleRoomType = (type: string) => {
    setSelectedRoomTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities((prev) =>
      prev.includes(amenity) ? prev.filter((a) => a !== amenity) : [...prev, amenity]
    );
  };

  const clearFilters = () => {
    setPriceRange([0, 1000]);
    setSelectedRoomTypes([]);
    setSelectedAmenities([]);
    setMinRating(null);
    setSelectedGenderType('');
    setFoodAvailable(null);
    setSelectedCity('');
    setSelectedHighlights([]);
    setCurrentPage(1);
    setShortTermPage(1);
    const params = new URLSearchParams(location.search);
    ['property_type', 'amenities', 'rating', 'price_min', 'price_max',
     'gender_type', 'food_available', 'city', 'highlights'].forEach(k => params.delete(k));
    navigate('?' + params.toString(), { replace: true });
  };

  const handlePropertyTypeChange = (e: ChangeEvent<HTMLInputElement>, type: string) => {
    console.log("Property type changed:", type, e.target.checked);
    const currentPropertyTypes = new URLSearchParams(location.search).getAll('property_type');
    const newPropertyTypes = e.target.checked
      ? [...currentPropertyTypes, type]
      : currentPropertyTypes.filter(t => t !== type);

    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('property_type');
    newPropertyTypes.forEach(t => queryParams.append('property_type', t));
    console.log("Navigating to:", '?' + queryParams.toString());
    navigate('?' + queryParams.toString());
  };

  useEffect(() => {
    setCurrentPage(1);
    setShortTermPage(1);
  }, [priceRange, selectedRoomTypes, selectedAmenities, minRating, sortBy, selectedGenderType, foodAvailable, selectedCity, selectedHighlights]);

  const activeFiltersCount =
    (priceRange[0] > 0 || priceRange[1] < 1000 ? 1 : 0) +
    selectedRoomTypes.length +
    selectedAmenities.length +
    (minRating ? 1 : 0) +
    (selectedGenderType ? 1 : 0) +
    (foodAvailable !== null ? 1 : 0) +
    (selectedCity ? 1 : 0) +
    selectedHighlights.length;

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const keywordParam = searchParams.get('keyword') || '';
    const maleGuestsParam = Number(searchParams.get('male_guests')) || 0;
    const femaleGuestsParam = Number(searchParams.get('female_guests')) || 0;
    const checkinParam = searchParams.get('checkin') || '';
    const checkoutParam = searchParams.get('checkout') || '';
    const accommodationTypeParam = searchParams.get('accommodation_type') || '';
    const priceRangeMinParam = Number(searchParams.get('price_min')) || 0;
    const priceRangeMaxParam = Number(searchParams.get('price_max')) || 1000;
    const propertyTypeParam = searchParams.getAll('property_type');
    const amenitiesParam = searchParams.getAll('amenities');
    const ratingParam = Number(searchParams.get('rating')) || 0;

    const genderTypeParam = searchParams.get('gender_type') || '';
    const foodAvailableParam = searchParams.get('food_available') || '';
    const cityParam = searchParams.get('city') || '';
    const highlightsParam = searchParams.getAll('highlights');

    setKeyword(keywordParam);
    setMaleGuests(maleGuestsParam);
    setFemaleGuests(femaleGuestsParam);
    setCheckInDate(checkinParam);
    setCheckOutDate(checkoutParam);
    setAccommodationType(accommodationTypeParam);
    setPriceRange([priceRangeMinParam, priceRangeMaxParam]);
    setSelectedRoomTypes(propertyTypeParam);
    setSelectedAmenities(amenitiesParam);
    setMinRating(ratingParam === 0 ? null : ratingParam);
    setSelectedGenderType(genderTypeParam);
    setFoodAvailable(foodAvailableParam === 'true' ? true : foodAvailableParam === 'false' ? false : null);
    setSelectedCity(cityParam);
    setSelectedHighlights(highlightsParam);
    setCurrentPage(1);
    setShortTermPage(1);

    if ((checkinParam && checkoutParam) || keywordParam) {
      const performSearch = async () => {
        try {
          const response = await getKeywordBasedSearch({
            keyword: keywordParam,
            male_guests: maleGuestsParam,
            female_guests: femaleGuestsParam,
            checkin: checkinParam,
            checkout: checkoutParam,
            accommodation_type: accommodationTypeParam,
            price_min: priceRangeMinParam,
            price_max: priceRangeMaxParam,
            property_type: propertyTypeParam,
            amenities: amenitiesParam,
            rating: ratingParam,
          });
          const mappedProperties: PropertyItem[] = response.map((item: any) => {
            const bedPrices = (item.beds ?? []).map((b: any) => parseFloat(b.price_per_bed)).filter((n: number) => !isNaN(n) && n > 0);
            const roomPrices = (item.rooms ?? []).map((r: any) => parseFloat(r.price_per_room)).filter((n: number) => !isNaN(n) && n > 0);
            const allPrices = [...bedPrices, ...roomPrices];
            const minPrice = allPrices.length > 0 ? Math.min(...allPrices) : null;
            return {
              id: item.id,
              name: item.name,
              location: item.location,
              price: minPrice !== null ? String(minPrice) : '',
              originalPrice: item.originalPrice,
              rating: item.average_rating,
              reviews: item.total_reviews ?? (item.reviews ? item.reviews.length : 0),
              image: item.images?.find((img: any) => img.is_primary)?.image || item.images?.[0]?.image,
              amenities: item.facilities || item.amenities || [],
              bedType: item.property_type,
              badge: item.badge,
              is_verified: item.is_verified,
              genderType: item.property_gender_type,
              foodAvailable: item.food_available,
              highlights: item.highlights || [],
              city: item.location?.city,
            };
          });
          setProperties(mappedProperties);
        } catch (error: any) {
          console.error("Error during API call:", error.message || error);
          if (!Array.isArray(error)) setProperties([]);
        }
      };
      performSearch();

      // Fetch short-term listings in parallel
      {
        const fetchShortTerm = async () => {
          setShortTermLoading(true);
          try {
            const accType = accommodationTypeParam === 'room' ? 'short_room' : 'short_bed';
            const cin = checkinParam || today;
            const cout = checkoutParam || tomorrow;
            const results = await getShortTermSearch({
              keyword: keywordParam,
              checkin: cin,
              checkout: cout,
              accommodation_type: accType,
              male_guests: maleGuestsParam,
              female_guests: femaleGuestsParam,
              price_min: priceRangeMinParam || undefined,
              price_max: priceRangeMaxParam < 1000 ? priceRangeMaxParam : undefined,
            });
            setShortTermProperties(Array.isArray(results) ? results : []);
          } catch {
            setShortTermProperties([]);
          } finally {
            setShortTermLoading(false);
          }
        };
        fetchShortTerm();
      }
    }
  }, [location.search]);

  const handleSearch = useCallback(() => {
    setErrorMessage('');

    if (!accommodationType) {
      setErrorMessage("Please select a booking type (Shared Bed or Private Room).");
      return;
    }

    const queryParams = new URLSearchParams();
    if (keyword) queryParams.append('keyword', keyword);
    if (maleGuests) queryParams.append('male_guests', maleGuests.toString());
    if (femaleGuests) queryParams.append('female_guests', femaleGuests.toString());
    if (checkInDate) queryParams.append('checkin', checkInDate);
    if (checkOutDate) queryParams.append('checkout', checkOutDate);
    if (accommodationType) queryParams.append('accommodation_type', accommodationType);

    if (priceRange[0] !== 0) queryParams.append('price_min', priceRange[0].toString());
    if (priceRange[1] !== 1000) queryParams.append('price_max', priceRange[1].toString());
    selectedRoomTypes.forEach(type => queryParams.append('property_type', type));
    selectedAmenities.forEach(amenity => queryParams.append('amenities', amenity));
    if (minRating !== null) queryParams.append('rating', minRating.toString());
    if (selectedGenderType) queryParams.append('gender_type', selectedGenderType);
    if (foodAvailable !== null) queryParams.append('food_available', foodAvailable.toString());
    if (selectedCity) queryParams.append('city', selectedCity);
    selectedHighlights.forEach(h => queryParams.append('highlights', h));

    navigate('/guest-search-results?' + queryParams.toString());

  }, [keyword, accommodationType, maleGuests, femaleGuests, checkInDate, checkOutDate, priceRange, selectedRoomTypes, selectedAmenities, minRating, selectedGenderType, foodAvailable, selectedCity, selectedHighlights, navigate]);

  // Both searches return the same properties; only show long-term results not already in the
  // short-term list (checked against the unfiltered list so filtered-out ones don't reappear here).
  const shortTermIds = new Set(shortTermProperties.map(p => p.id));
  const filteredAndSortedProperties = properties
    .filter((property) => !shortTermIds.has(property.id))
    .filter((property) => {
      const parsedPrice = parseFloat(property.price);
      const inPriceRange = !property.price || isNaN(parsedPrice) || (parsedPrice >= priceRange[0] && parsedPrice <= priceRange[1]);
      const matchesRoomType = selectedRoomTypes.length === 0 || (!!property.bedType && selectedRoomTypes.includes(property.bedType));
      const matchesAmenities = selectedAmenities.length === 0 || selectedAmenities.every((a) => property.amenities.includes(a));
      const matchesRating = !minRating || property.rating >= minRating;
      const matchesGender = !selectedGenderType || property.genderType === selectedGenderType;
      const matchesFood = foodAvailable === null || property.foodAvailable === foodAvailable;
      const matchesCity = !selectedCity || property.city?.toLowerCase() === selectedCity.toLowerCase();
      const matchesHighlights = selectedHighlights.length === 0 ||
        selectedHighlights.every(h => property.highlights?.includes(h));
      return inPriceRange && matchesRoomType && matchesAmenities && matchesRating &&
             matchesGender && matchesFood && matchesCity && matchesHighlights;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "price-low":
          return parseFloat(a.price) - parseFloat(b.price);
        case "price-high":
          return parseFloat(b.price) - parseFloat(a.price);
        case "rating":
          return b.rating - a.rating;
        case "reviews":
          return b.reviews - a.reviews;
        case "recommended":
        default:
          return b.rating * 100 + b.reviews - (a.rating * 100 + a.reviews);
      }
    });

  // Apply sidebar filters + sort to short-term results
  const filteredShortTermProperties = shortTermProperties
    .filter(p => {
      const prices = [
        ...p.vacant_beds.map(b => Number(b.price_per_bed)),
        ...p.vacant_rooms.map(r => Number(r.price_per_room)),
      ].filter(n => !isNaN(n) && n > 0);
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const inPriceRange = priceRange[0] === 0 && priceRange[1] === 1000
        ? true
        : minPrice >= priceRange[0] && minPrice <= priceRange[1];
      const matchesRating = !minRating || Number(p.average_rating) >= minRating;
      const matchesPropertyType = selectedRoomTypes.length === 0 ||
        (!!p.property_type && selectedRoomTypes.includes(p.property_type));
      const propFacilities = (p.facilities ?? []).map(f => f.toLowerCase());
      const matchesAmenities = selectedAmenities.length === 0 ||
        selectedAmenities.every(a => propFacilities.includes(a.toLowerCase()));
      const matchesGender = !selectedGenderType ||
        (!!p.property_gender_type && p.property_gender_type === selectedGenderType);
      const matchesFood = foodAvailable === null || p.food_available === foodAvailable;
      const matchesCity = !selectedCity ||
        p.location?.city?.toLowerCase() === selectedCity.toLowerCase();
      const matchesHighlights = selectedHighlights.length === 0 ||
        selectedHighlights.every(h => (p.highlights ?? []).includes(h));
      return inPriceRange && matchesRating && matchesPropertyType && matchesAmenities &&
             matchesGender && matchesFood && matchesCity && matchesHighlights;
    })
    .sort((a, b) => {
      const minPriceOf = (p: typeof a) => {
        const prices = [
          ...p.vacant_beds.map(bd => Number(bd.price_per_bed)),
          ...p.vacant_rooms.map(r => Number(r.price_per_room)),
        ].filter(n => !isNaN(n) && n > 0);
        return prices.length > 0 ? Math.min(...prices) : 0;
      };
      switch (sortBy) {
        case 'price-low':  return minPriceOf(a) - minPriceOf(b);
        case 'price-high': return minPriceOf(b) - minPriceOf(a);
        case 'rating':     return Number(b.average_rating) - Number(a.average_rating);
        case 'reviews':    return (b.total_reviews ?? 0) - (a.total_reviews ?? 0);
        default:
          return (Number(b.average_rating) * 100 + (b.total_reviews ?? 0)) -
                 (Number(a.average_rating) * 100 + (a.total_reviews ?? 0));
      }
    });

  const longTermTotalPages = Math.ceil(filteredAndSortedProperties.length / ITEMS_PER_PAGE);
  const paginatedProperties = filteredAndSortedProperties.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const shortTermTotalPages = Math.ceil(filteredShortTermProperties.length / ITEMS_PER_PAGE);
  const paginatedShortTerm = filteredShortTermProperties.slice(
    (shortTermPage - 1) * ITEMS_PER_PAGE,
    shortTermPage * ITEMS_PER_PAGE
  );

  const FilterSidebar = ({ className = "", handlePropertyTypeChange }: { className?: string, handlePropertyTypeChange: (e: ChangeEvent<HTMLInputElement>, type: string) => void }) => (
    <div className={`space-y-6 ${className}`}>
      {/* Price Range */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <SlidersHorizontal size={16} />
            Price Range
          </h3>
          <span className="text-sm text-primary font-medium">
            ₹{priceRange[0]} - ₹{priceRange[1]}
          </span>
        </div>
        <Slider
          defaultValue={[0, 1000]}
          max={1000}
          min={0}
          step={50}
          value={priceRange}
          onValueChange={setPriceRange}
          className="w-full"
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>₹0</span>
          <span>₹1000+</span>
        </div>
      </div>

      {/* Rating Filter */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Star size={16} />
          Rating
        </h3>
        <div className="space-y-2">
          {RATING_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setMinRating(minRating === option.value ? null : option.value)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                minRating === option.value
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border hover:border-primary/50 hover:bg-muted"
              }`}
            >
              <div className="flex items-center gap-2">
                <Star
                  size={14}
                  className={minRating === option.value ? "fill-primary text-primary" : "text-muted-foreground"}
                />
                <span className="text-sm">{option.label}</span>
              </div>
              {minRating === option.value && (
                <div className="w-2 h-2 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Property Type */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Bed size={16} />
          Property Type
        </h3>
        <div className="space-y-2">
          {PROPERTY_TYPES.map(({ value, label }) => (
            <label key={value} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                id={`property-${value}`}
                checked={selectedRoomTypes.includes(value)}
                onCheckedChange={(checked) => {
                  const event = { target: { checked } } as React.ChangeEvent<HTMLInputElement>;
                  handlePropertyTypeChange(event, value);
                }}
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Gender Type */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Users size={16} />
          Gender Type
        </h3>
        <div className="space-y-2">
          {[
            { value: 'male',   label: 'Male Only' },
            { value: 'female', label: 'Female Only' },
            { value: 'unisex', label: 'Co-ed' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setSelectedGenderType(selectedGenderType === opt.value ? '' : opt.value)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all ${
                selectedGenderType === opt.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <span className="text-sm">{opt.label}</span>
              {selectedGenderType === opt.value && <div className="w-2 h-2 rounded-full bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {/* Food Available */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <UtensilsCrossed size={16} />
          Food Included
        </h3>
        <div className="flex gap-2">
          {[{ value: true, label: 'Yes' }, { value: false, label: 'No' }].map(opt => (
            <button
              key={String(opt.value)}
              onClick={() => setFoodAvailable(foodAvailable === opt.value ? null : opt.value)}
              className={`flex-1 py-2 rounded-lg border text-sm font-medium transition-all ${
                foodAvailable === opt.value
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50 hover:bg-muted text-muted-foreground'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* City */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <MapPin size={16} />
          City
        </h3>
        <div className="space-y-2">
          {['bangalore', 'mumbai', 'delhi', 'hyderabad', 'chennai', 'pune'].map(city => (
            <button
              key={city}
              onClick={() => setSelectedCity(selectedCity === city ? '' : city)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all capitalize text-sm ${
                selectedCity === city
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:border-primary/50 hover:bg-muted text-muted-foreground'
              }`}
            >
              <span>{city.charAt(0).toUpperCase() + city.slice(1)}</span>
              {selectedCity === city && <div className="w-2 h-2 rounded-full bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {/* Amenities (Facilities) */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Wifi size={16} />
          Amenities
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(amenityLabels).map(([key, label]) => {
            const Icon = amenityIcons[key];
            return (
              <button
                key={key}
                onClick={() => toggleAmenity(key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                  selectedAmenities.includes(key)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:border-primary/50 hover:bg-muted text-muted-foreground'
                }`}
              >
                {Icon && <Icon size={14} />}
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Highlights / Nearby */}
      <div className="space-y-3">
        <h3 className="font-semibold text-foreground flex items-center gap-2">
          <Train size={16} />
          Nearby
        </h3>
        <div className="space-y-2">
          {HIGHLIGHTS.map(({ value, label, icon: Icon }) => (
            <label key={value} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                id={`highlight-${value}`}
                checked={selectedHighlights.includes(value)}
                onCheckedChange={() =>
                  setSelectedHighlights(prev =>
                    prev.includes(value) ? prev.filter(h => h !== value) : [...prev, value]
                  )
                }
                className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <Icon size={14} className="text-muted-foreground shrink-0" />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {label}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Clear Filters */}
      {activeFiltersCount > 0 && (
        <Button variant="outline" className="w-full" onClick={clearFilters}>
          <X size={14} className="mr-2" />
          Clear All Filters ({activeFiltersCount})
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {state.role === 'guest' ? <LandingHeader /> : <LandingPageHeader isMobileMenuOpen={false} setIsMobileMenuOpen={() => {}} />}

      {/* Search Header */}
      <section className="pt-24 pb-6 bg-gradient-to-b from-primary/10 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                {keyword ? `Stays in "${keyword}"` : "All Properties"}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {filteredShortTermProperties.length + filteredAndSortedProperties.length} properties found
              </p>
            </div>

            {/* Search Bar */}
            <div className="bg-card border border-border rounded-2xl shadow-lg p-3">
              <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">

                {/* Location */}
                <div className="flex items-center gap-2 flex-1 px-3 py-2 rounded-xl bg-background border border-border/60 hover:border-primary/40 transition-colors">
                  <MapPin size={16} className="text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Location</p>
                    <Input
                      placeholder="Where are you going?"
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="border-0 bg-transparent p-0 h-5 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60"
                    />
                  </div>
                </div>

                <div className="hidden lg:block w-px h-10 bg-border/60 self-center" />

                {/* Check-in */}
                <div className="px-3 py-2 rounded-xl bg-background border border-border/60 hover:border-primary/40 transition-colors lg:w-40">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Check-in</p>
                  <input
                    type="date"
                    value={checkInDate}
                    min={today}
                    onChange={(e) => {
                      setCheckInDate(e.target.value);
                      if (checkOutDate && e.target.value >= checkOutDate) setCheckOutDate('');
                    }}
                    className="w-full text-sm font-medium bg-transparent border-0 outline-none text-foreground mt-0.5"
                  />
                </div>

                <div className="hidden lg:block w-px h-10 bg-border/60 self-center" />

                {/* Check-out */}
                <div className="px-3 py-2 rounded-xl bg-background border border-border/60 hover:border-primary/40 transition-colors lg:w-40">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Check-out</p>
                  <input
                    type="date"
                    value={checkOutDate}
                    min={checkInDate || tomorrow}
                    onChange={(e) => setCheckOutDate(e.target.value)}
                    className="w-full text-sm font-medium bg-transparent border-0 outline-none text-foreground mt-0.5"
                  />
                </div>

                <div className="hidden lg:block w-px h-10 bg-border/60 self-center" />

                {/* Stay Type */}
                <div className="px-3 py-2 rounded-xl bg-background border border-border/60 hover:border-primary/40 transition-colors lg:w-38">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Stay Type</p>
                  <Select value={accommodationType} onValueChange={setAccommodationType}>
                    <SelectTrigger className="border-0 bg-transparent p-0 h-5 text-sm font-medium focus:ring-0 shadow-none [&>svg]:ml-1 [&>svg]:h-3 [&>svg]:w-3">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bed">Shared Bed</SelectItem>
                      <SelectItem value="room">Private Room</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="hidden lg:block w-px h-10 bg-border/60 self-center" />

                {/* Guests */}
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-background border border-border/60 hover:border-primary/40 transition-colors cursor-pointer lg:w-36">
                      <div>
                        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Guests</p>
                        <p className="text-sm font-medium text-foreground mt-0.5">
                          {maleGuests + femaleGuests > 0
                            ? `${maleGuests + femaleGuests} guest${maleGuests + femaleGuests > 1 ? 's' : ''}`
                            : 'Add guests'}
                        </p>
                      </div>
                      <Users size={14} className="text-muted-foreground" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-60 p-4" align="start">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Male Guests</p>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-7 w-7 text-base" onClick={() => setMaleGuests(Math.max(0, maleGuests - 1))}>−</Button>
                          <span className="w-5 text-center text-sm font-medium">{maleGuests}</span>
                          <Button variant="outline" size="icon" className="h-7 w-7 text-base" onClick={() => setMaleGuests(maleGuests + 1)}>+</Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Female Guests</p>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="icon" className="h-7 w-7 text-base" onClick={() => setFemaleGuests(Math.max(0, femaleGuests - 1))}>−</Button>
                          <span className="w-5 text-center text-sm font-medium">{femaleGuests}</span>
                          <Button variant="outline" size="icon" className="h-7 w-7 text-base" onClick={() => setFemaleGuests(femaleGuests + 1)}>+</Button>
                        </div>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Search Button */}
                <Button
                  onClick={handleSearch}
                  className="rounded-xl px-6 py-5 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-2 shrink-0"
                >
                  <Search size={16} />
                  Search
                </Button>
              </div>

              {errorMessage && (
                <p className="text-sm text-red-500 mt-2 px-3">{errorMessage}</p>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Top Bar with Filter Toggle and Sort */}
      <section className="sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Mobile Filter Sheet */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="lg:hidden">
                    <Filter size={16} className="mr-2" />
                    Filters
                    {activeFiltersCount > 0 && (
                      <Badge className="ml-2 bg-primary text-primary-foreground h-5 w-5 p-0 flex items-center justify-center text-xs">
                        {activeFiltersCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Filter size={18} />
                      Filters
                    </SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FilterSidebar handlePropertyTypeChange={handlePropertyTypeChange} />
                  </div>
                </SheetContent>
              </Sheet>

              {/* Desktop Sidebar Toggle */}
              <Button
                variant="outline"
                size="sm"
                className="hidden lg:flex"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                <Filter size={16} className="mr-2" />
                {sidebarOpen ? "Hide Filters" : "Show Filters"}
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-primary text-primary-foreground h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {activeFiltersCount}
                  </Badge>
                )}
              </Button>

              {/* Results count */}
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {filteredShortTermProperties.length + filteredAndSortedProperties.length} properties found
              </span>
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-44">
                <ArrowUpDown size={14} className="mr-2" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recommended">Recommended</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="reviews">Most Reviews</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex gap-8">

            {/* Desktop Sidebar — shown for both tabs */}
            <motion.aside
              initial={{ width: 280, opacity: 1 }}
              animate={{ width: sidebarOpen ? 280 : 0, opacity: sidebarOpen ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="hidden lg:block flex-shrink-0 overflow-hidden"
            >
              <div className="sticky top-36 bg-card rounded-2xl border border-border/50 p-6 shadow-sm">
                <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Filter size={18} />
                  Filters
                </h2>
                <FilterSidebar handlePropertyTypeChange={handlePropertyTypeChange} />
              </div>
            </motion.aside>

            {/* Results Area */}
            <div className="flex-1 min-w-0">

              {/* Short-term Results */}
              <>
                  {shortTermLoading ? (
                    <div className="flex items-center justify-center py-16 gap-3">
                      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span className="text-muted-foreground">Finding properties...</span>
                    </div>
                  ) : filteredShortTermProperties.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-16"
                    >
                      <div className="text-6xl mb-4">🛏️</div>
                      <h3 className="text-xl font-semibold mb-2">
                        {shortTermProperties.length > 0 ? 'No results match your filters' : 'No properties found'}
                      </h3>
                      <p className="text-muted-foreground mb-4">
                        {shortTermProperties.length > 0 ? 'Try adjusting your filters' : 'Try a different location or date range'}
                      </p>
                      {shortTermProperties.length > 0 && activeFiltersCount > 0 && (
                        <Button onClick={clearFilters} variant="outline">Clear All Filters</Button>
                      )}
                    </motion.div>
                  ) : (
                    <div className={`grid gap-6 ${sidebarOpen ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
                      {paginatedShortTerm.map((property, index) => {
                        const image = property.images?.find(img => img.is_primary)?.image || property.images?.[0]?.image || DEFAULT_PROPERTY_IMAGE;
                        const locationStr = [property.location?.locality, property.location?.city]
                          .filter(Boolean).join(', ') || property.location?.street_address || '';
                        const bedFromPrice = property.vacant_beds.length > 0
                          ? Math.min(...property.vacant_beds.map(b => Number(b.price_per_bed))) : null;
                        const roomFromPrice = property.vacant_rooms.length > 0
                          ? Math.min(...property.vacant_rooms.map(r => Number(r.price_per_room))) : null;
                        const fromPrice = bedFromPrice !== null && roomFromPrice !== null
                          ? Math.min(bedFromPrice, roomFromPrice) : bedFromPrice ?? roomFromPrice;
                        const totalAvailableBeds = property.vacant_beds.reduce((s, b) => s + b.vacant_beds, 0);
                        const totalAvailableRooms = property.vacant_rooms.reduce((s, r) => s + r.vacant_rooms, 0);
                        const stAccType = accommodationType === 'short_room' ? 'short_room' : 'short_bed';
                        const detailUrl = `/property/${property.id}?keyword=${keyword}&checkin=${checkInDate}&checkout=${checkOutDate}&accommodation_type=${stAccType}`;
                        const gender = property.property_gender_type ? genderTypeConfig[property.property_gender_type.toLowerCase()] : undefined;

                        return (
                          <motion.div
                            key={property.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05 }}
                            className="bg-card rounded-2xl border border-border/50 overflow-hidden shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
                            onClick={() => navigate(detailUrl)}
                          >
                            <div className="relative h-48 overflow-hidden">
                              <img
                                src={image}
                                alt={property.name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_PROPERTY_IMAGE; }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                              {Number(property.average_rating) > 0 && (
                                <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded-full text-xs">
                                  <Star size={11} className="fill-yellow-400 text-yellow-400" />
                                  {Number(property.average_rating).toFixed(1)}
                                </div>
                              )}
                              {gender && (
                                <div
                                  className={`absolute bottom-3 right-3 backdrop-blur-sm rounded-lg px-2 py-1 text-xs flex items-center gap-1 shadow-md ${gender.className}`}
                                  title={`${gender.label} property`}
                                >
                                  <gender.icon size={12} />
                                  {gender.label}
                                </div>
                              )}
                            </div>
                            <div className="p-4 space-y-3">
                              <div>
                                <div className="flex items-center gap-1 min-w-0">
                                  <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-1">{property.name}</h3>
                                  {property.is_verified && (
                                    <span title="Verified property" className="flex-shrink-0 flex items-center gap-0.5 text-green-600 text-xs font-medium">
                                      <BadgeCheck size={14} className="fill-green-100" />
                                      Verified
                                    </span>
                                  )}
                                </div>
                                {locationStr && (
                                  <div className="flex items-center gap-1 mt-1 text-muted-foreground text-xs">
                                    <MapPin size={11} />
                                    <span className="line-clamp-1">{locationStr}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {totalAvailableBeds > 0 && (
                                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full border border-blue-100">
                                    <Bed size={11} />{totalAvailableBeds} bed{totalAvailableBeds > 1 ? 's' : ''}
                                  </span>
                                )}
                                {totalAvailableRooms > 0 && (
                                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded-full border border-purple-100">
                                    <Building2 size={11} />{totalAvailableRooms} room{totalAvailableRooms > 1 ? 's' : ''}
                                  </span>
                                )}
                                {property.food_available && (
                                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full border border-orange-100">
                                    <UtensilsCrossed size={11} />Food included
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center justify-between pt-1">
                                <div>
                                  {fromPrice !== null ? (
                                    <>
                                      <span className="text-xs text-muted-foreground">from</span>
                                      <span className="ml-1 font-bold text-foreground text-base">₹{fromPrice.toFixed(0)}</span>
                                      <span className="text-xs text-muted-foreground">/night</span>
                                    </>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">Price on request</span>
                                  )}
                                </div>
                                <Link
                                  to={detailUrl}
                                  onClick={(e) => e.stopPropagation()}
                                  className="px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                                >
                                  View Details
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </>

              {/* Short-term Pagination */}
              {shortTermTotalPages > 1 && (
                <Pagination
                  currentPage={shortTermPage}
                  totalPages={shortTermTotalPages}
                  totalItems={filteredShortTermProperties.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setShortTermPage}
                />
              )}

              {/* Long-term Results */}
              {filteredAndSortedProperties.length > 0 && (
                <>
                  <div className={`grid gap-6 mt-6 ${sidebarOpen ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}>
                    {paginatedProperties.map((property, index) => (
                      <PropertyCard
                        key={property.id}
                        property={property}
                        toggleFavorite={toggleFavorite}
                        isFavorite={favorites.includes(property.id)}
                        index={index}
                        keyword={keyword}
                        male_guests={maleGuests}
                        female_guests={femaleGuests}
                        checkin={checkInDate}
                        checkout={checkOutDate}
                        accommodation_type={accommodationType}
                      />
                    ))}
                  </div>
                  {longTermTotalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={longTermTotalPages}
                      totalItems={filteredAndSortedProperties.length}
                      itemsPerPage={ITEMS_PER_PAGE}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </>
              )}
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuestSearchResults;
